import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/shared/lib/cn";

/**
 * Square icon-only control (topbar utils, message actions, composer tools).
 * Pass a single icon as children; sizing/centering is handled here.
 */
export const iconButtonVariants = cva(
  "inline-grid place-items-center shrink-0 text-ink outline-none transition-[background,color] duration-150 ease-house hover:bg-line-soft hover:text-ink focus-visible:ring-[3px] focus-visible:ring-tint disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      size: {
        sm: "size-8 rounded-md [&_svg]:size-4",
        md: "size-9 rounded-md [&_svg]:size-[18px]",
      },
    },
    defaultVariants: { size: "md" },
  },
);

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants> {
  /** Required for a11y — icon buttons have no text. */
  "aria-label": string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton({ className, size, type = "button", ...props }, ref) {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(iconButtonVariants({ size }), className)}
        {...props}
      />
    );
  },
);
